<template>
  <WhiteIQOS
    :title="
      isGeneralSchedule
        ? $t('schedule.general_title')
        : $t('schedule.specific_title', {
            mode: modeDisplayName,
          })
    "
    :subtitle="
      isGeneralSchedule
        ? $t('schedule.general_desc')
        : $t('schedule.specific_desc', {
            mode: modeDisplayName,
          })
    "
    :enabled="isGeneralSchedule ? true : schedule.enabled"
    bodyClass="gap-row-txs"
    v-if="schedule"
  >
    <template #actions>
      <IQOSToggle v-model="schedule.enabled" v-if="!isGeneralSchedule" />
    </template>

    <div class="panel-header">
      <div
        class="panel-header-item qrw-label-md qrw-text-content-base-tertiary"
      >
        {{ $t("schedule.from") }}
      </div>
      <div
        class="panel-header-item qrw-label-md qrw-text-content-base-tertiary"
      >
        {{ $t("schedule.to") }}
      </div>
    </div>

    <template v-for="dayName in dayNamesConstants">
      <BaseDivider color="border-base-tertiary" :key="'div' + dayName" />
      <div class="schedule-row" :key="dayName">
        <BaseBetaCheckbox
          v-model="schedule[dayName].workday"
          size="lg"
          class="schedule-checkbox w-100"
        >
          {{ $t("schedule.days." + dayName) }}
        </BaseBetaCheckbox>
        <BaseTimepickerInput
          v-model="schedule[dayName].start"
          size="md"
          class="schedule-row-time"
          :disabled="!schedule[dayName].workday"
        />
        <BaseTimepickerInput
          v-model="schedule[dayName].end"
          size="md"
          class="schedule-row-time"
          :disabled="!schedule[dayName].workday"
        />
      </div>
    </template>
  </WhiteIQOS>
</template>

<script>
import moment from "moment";

const makeEmptyDay = () => ({
  workday: false,
  startMinute: 0,
  endMinute: 0,
  startHour: 0,
  endHour: 0,
});

export default {
  props: {
    mode: String,
  },
  data() {
    return {
      schedule: null,
      dayNamesConstants: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
      canUpdate: false,

      lastAuditWriting: 0
    };
  },
  watch: {
    schedule: {
      handler() {
        this.save();
      },
      deep: true,
    },
  },
  methods: {
    writeAuditEntry() {
      this.$store.dispatch("auditlog/write", {
        type: "cafe_schedule",
        payload: {
          mode: this.mode || "general",
          schedule: this.schedule,
        },
      });
    },
    save() {
      if (!this.canUpdate) return;

      const output = this.toApiSchedule();

      if (this.isGeneralSchedule) {
        this.$store.commit("patchCafeGeneralData", {
          schedule: output,
        });
      } else {
        if (this.mode == "preorder") {
          this.$store.commit("setPreorderSchedule", output);
        }

        if (this.mode == "delivery") {
          this.$store.commit("setDeliverySchedule", output);
        }
      }

      this.$store.dispatch("saveCafe");

      if (Date.now() - this.lastAuditWriting > 3000) {
        this.writeAuditEntry();
        this.lastAuditWriting = Date.now();
      }
    },
    toApiSchedule() {
      const result = {
        enabled: this.isGeneralSchedule ? true : this.schedule.enabled || false,
      };

      for (const key in this.schedule) {
        if (!this.dayNamesConstants.includes(key)) {
          continue;
        }

        if (this.schedule.hasOwnProperty(key)) {
          const item = this.schedule[key];

          if (!item || !item.start) {
            result[key] = makeEmptyDay();
            continue;
          }

          result[key] = {
            workday: item.workday,
            startHour: moment(item.start).hours(),
            startMinute: moment(item.start).minutes(),
            endHour: moment(item.end).hours(),
            endMinute: moment(item.end).minutes(),
          };
        }
      }

      return result;
    },
    toLocalSchedule(_input) {
      let input = _input;

      if (!_input) {
        input = {
          enabled: false,
          mon: makeEmptyDay(),
          tue: makeEmptyDay(),
          wed: makeEmptyDay(),
          thu: makeEmptyDay(),
          fri: makeEmptyDay(),
          sat: makeEmptyDay(),
          sun: makeEmptyDay(),
        };
      }

      const result = {
        enabled: input.enabled || false,
      };

      for (const key in input) {
        if (!this.dayNamesConstants.includes(key)) {
          continue;
        }

        if (input.hasOwnProperty(key)) {
          let item = input[key];

          const isNonNullItem =
            item.startHour !== null && item.endHour !== null;

          result[key] = {
            workday: item.workday,
            start: isNonNullItem
              ? moment()
                  .minutes(item.startMinute)
                  .hours(item.startHour)
                  .toDate()
              : null,
            end: isNonNullItem
              ? moment()
                  .minutes(item.endMinute)
                  .hours(item.endHour)
                  .toDate()
              : null,
          };
        }
      }

      this.schedule = result;
    },
  },
  created() {
    if (this.isGeneralSchedule) {
      this.toLocalSchedule(this.cafe.schedule);
    } else {
      this.toLocalSchedule(this.cafe[this.mode].schedule);
    }
  },
  mounted() {
    setTimeout(() => {
      this.canUpdate = true;
    }, 1000);
  },
  computed: {
    cafe() {
      return this.$store.state.cafe;
    },
    isGeneralSchedule() {
      return !this.mode;
    },
    modeDisplayName() {
      if (!this.mode) return null;

      return this.$store.getters["subscriptions/getModeDisplayTitle"](
        this.mode
      );
    },
  },
};
</script>

<style scoped>
.panel-header {
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  column-gap: 24px;
}

.panel-header-item {
  display: flex;
  width: 132px;
  align-items: center;
}

.schedule-row {
  display: flex;
  flex-direction: row;
  column-gap: 24px;
  align-items: center;
  justify-content: flex-start;
}

.schedule-row-time {
  width: 132px;
  min-width: 132px;
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  align-items: center;
}

.schedule-checkbox {
  justify-content: flex-start !important;
}
</style>
